// Super-admin Threat Insights — active suppressions manager (TI-9).
//
// Backs the CRUD routes at /api/admin/threat-insights/suppressions:
//   GET    /suppressions?active=true&rule_id&site_id   list
//   PATCH  /suppressions/:id                           extend (bump expires_at)
//   DELETE /suppressions/:id                           revoke
//
// Design notes:
//   * A suppression is scoped by (rule_id, site_id?, subject_value?).
//     NULL fields are "wildcard" — surfaced as "All sites" / "All
//     subjects" in the table.
//   * "Extend" bumps expires_at by N hours from *now*, not from the
//     row's current expires_at. Rationale: operators extend a
//     suppression because the noise is still active — anchoring to
//     the current wall clock is what they expect.
//   * "Revoke" hard-deletes. The scanner reads suppressions on every
//     rule-eval, so a delete takes effect on the next 5-min tick.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, TextField, MenuItem, Select, FormControl,
  InputLabel, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, IconButton, Tooltip, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";

import useAuth from "../../../../hooks/useAuth";
import threatInsightAdmin from "../../../../utils/axios/v1/threatInsightAdminAxios";
import PageHeader from "../../../../components/pages/_new/super/common/PageHeader";

const SCOPE_LABEL = {
  ip:       "IP",
  identity: "Identity",
  apikey:   "API key",
  session:  "Session",
  host:     "Host",
  global:   "Cross-site",
};

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); }
  catch { return String(v); }
}

function isExpired(v) {
  if (!v) return false;
  try { return new Date(v).getTime() < Date.now(); }
  catch { return false; }
}

export default function T_ThreatInsightSuppressions() {
  const { isAuthenticated } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [filterRule, setFilterRule] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const [extendTarget, setExtendTarget] = useState(null);
  const [extendHours, setExtendHours] = useState(168);
  const [busyId, setBusyId] = useState(null);

  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (filterRule) params.rule_id = filterRule;
      if (filterSite) params.site_id = filterSite;
      if (showActiveOnly) params.active = "true";
      const res = await threatInsightAdmin.listSuppressions(params);
      setRows(Array.isArray(res?.data?.items) ? res.data.items : []);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [filterRule, filterSite, showActiveOnly]);

  useEffect(() => { if (isAuthenticated) refresh(); }, [isAuthenticated, refresh]);

  const activeCount = useMemo(() => rows.filter((r) => !isExpired(r.expires_at)).length, [rows]);

  const doExtend = async () => {
    if (!extendTarget) return;
    const id = extendTarget.id;
    setBusyId(id);
    try {
      const newExpires = new Date(Date.now() + Math.max(1, Number(extendHours) || 168) * 3600_000);
      await threatInsightAdmin.extendSuppression(id, { expires_at: newExpires });
      setSnack({ open: true, severity: "success", message: "Suppression extended" });
      setExtendTarget(null);
      refresh();
    } catch (e) {
      setSnack({ open: true, severity: "error", message: e?.response?.data?.error || e?.message || "Extend failed" });
    } finally {
      setBusyId(null);
    }
  };

  const doRevoke = async (row) => {
    if (!window.confirm(`Revoke suppression on ${row.rule_id}? The scanner will start considering this rule again on its next tick.`)) return;
    setBusyId(row.id);
    try {
      await threatInsightAdmin.deleteSuppression(row.id);
      setSnack({ open: true, severity: "success", message: "Suppression revoked" });
      refresh();
    } catch (e) {
      setSnack({ open: true, severity: "error", message: e?.response?.data?.error || e?.message || "Revoke failed" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Helmet title="Threat Insights — Suppressions" />
      <Box sx={{ p: 3 }}>
        <PageHeader
          icon={<VolumeOffOutlinedIcon fontSize="large" />}
          title="Threat Insight suppressions"
          description="Operator-created silences on threat-insight rules. Scoped by rule + site + subject. Auto-expires at expires_at."
        />

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3, mb: 2, flexWrap: "wrap", gap: 1 }}>
          <TextField
            size="small"
            label="Rule ID"
            value={filterRule}
            onChange={(e) => setFilterRule(e.target.value)}
            placeholder="e.g. REC-003"
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            label="Site ID"
            value={filterSite}
            onChange={(e) => setFilterSite(e.target.value)}
            placeholder="site UUID"
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Show</InputLabel>
            <Select
              label="Show"
              value={showActiveOnly ? "active" : "all"}
              onChange={(e) => setShowActiveOnly(e.target.value === "active")}
            >
              <MenuItem value="active">Active only</MenuItem>
              <MenuItem value="all">All (incl. expired)</MenuItem>
            </Select>
          </FormControl>
          <Button
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={refresh}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Refresh
          </Button>
          <Chip
            label={`${activeCount} active`}
            size="small"
            sx={{ ml: "auto", fontWeight: 600, background: "#EDF3FE", color: "#0244BE" }}
          />
        </Stack>

        {err && (
          <Box sx={{ mb: 2, p: 2, background: "#FEECEE", color: "#A11228", borderRadius: 1 }}>
            {err}
          </Box>
        )}

        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E5E5E5", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#FAFAFA" }}>
                <TableCell><strong>Rule</strong></TableCell>
                <TableCell><strong>Site</strong></TableCell>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
                <TableCell><strong>Created by</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Expires</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: "#7B7B7B" }}>
                  No suppressions {showActiveOnly ? "active" : "found"}.
                </TableCell></TableRow>
              ) : (
                rows.map((r) => {
                  const expired = isExpired(r.expires_at);
                  return (
                    <TableRow key={r.id} hover sx={{ opacity: expired ? 0.55 : 1 }}>
                      <TableCell><code>{r.rule_id}</code></TableCell>
                      <TableCell>{r.site_id
                        ? <Tooltip title={r.site_id}><span>{r.site_id.slice(0, 8)}…</span></Tooltip>
                        : <Chip label="All sites" size="small" sx={{ height: 20, fontSize: 11 }} />
                      }</TableCell>
                      <TableCell>
                        {r.subject_value ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {r.subject_kind && (
                              <Chip label={SCOPE_LABEL[r.subject_kind] || r.subject_kind} size="small" sx={{ height: 20, fontSize: 10.5 }} />
                            )}
                            <code style={{ fontSize: 12 }}>{r.subject_value}</code>
                          </Stack>
                        ) : (
                          <Chip label="Every subject" size="small" sx={{ height: 20, fontSize: 11 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240, fontSize: 12 }}>
                        <Tooltip title={r.reason || ""}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                            {r.reason || "—"}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.created_by || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDate(r.created_at)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {expired
                          ? <Chip label="Expired" size="small" sx={{ height: 20, fontSize: 10.5, background: "#F4F4F4", color: "#7B7B7B" }} />
                          : (r.expires_at ? fmtDate(r.expires_at) : <Chip label="Never" size="small" sx={{ height: 20, fontSize: 10.5 }} />)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Extend expiry">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => { setExtendHours(168); setExtendTarget(r); }}
                              disabled={busyId === r.id}
                            >
                              <ScheduleOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revoke suppression">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => doRevoke(r)}
                              disabled={busyId === r.id}
                              sx={{ color: "#A11228" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={!!extendTarget} onClose={busyId ? undefined : () => setExtendTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Extend suppression</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: 13, color: "#606060" }}>
              Rule: <strong>{extendTarget?.rule_id}</strong>{extendTarget?.site_id ? <> · site <code>{extendTarget.site_id.slice(0, 8)}…</code></> : " · all sites"}
            </Typography>
            <TextField
              label="Extend by (hours from now)"
              type="number"
              size="small"
              value={extendHours}
              onChange={(e) => setExtendHours(e.target.value)}
              inputProps={{ min: 1, max: 24 * 30 }}
              helperText="Max 720h (30 days). Sets a fresh expires_at anchored at now()."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendTarget(null)} disabled={!!busyId} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={doExtend} disabled={!!busyId} variant="contained" sx={{ textTransform: "none" }}>
            {busyId ? "Extending…" : "Extend"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
